import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { Result } from "better-result";
import { z } from "zod/v4";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { toResult } from "#/modules/util.auth/api-errors";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

/** Section layout + info tab: the full school record for the current slug. */
export const getSchoolDetail = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ school: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.GET("/scholen/{slug}", {
        params: { path: { slug: data.school } },
      }),
    );

    if (Result.isError(result)) {
      throw result.error;
    }

    return { school: result.value?.school, canEdit: result.value?.canEdit ?? false };
  });

/** Docenten tab: active docenten + pending invites + seat counts. */
export const getDocentenOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const [docentenResult, invitesResult] = await Promise.all([
      client.GET("/school/docenten").then(toResult),
      client.GET("/school/docenten/invites").then(toResult),
    ]);

    if (Result.isError(docentenResult)) {
      throw docentenResult.error;
    }

    if (Result.isError(invitesResult)) {
      throw invitesResult.error;
    }

    return {
      docenten: docentenResult.value?.docenten ?? [],
      canAddDocent: docentenResult.value?.canAddDocent ?? false,
      currentDocenten: docentenResult.value?.currentDocenten ?? 0,
      maxDocenten: docentenResult.value?.maxDocenten ?? 0,
      invites: invitesResult.value?.invites ?? [],
    };
  });

export const submitSetHoofddocent = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ schoolDocentId: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/school/docenten/hoofddocent", {
        body: { schoolDocentId: data.schoolDocentId },
      }),
    );

    if (Result.isError(result)) {
      throw new Error(m["school.docenten.errors.set_hoofddocent_failed"]());
    }
  });

export const submitRemoveDocent = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ schooldocentId: z.string() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.DELETE("/school/docenten", {
        body: { schooldocentId: data.schooldocentId },
      }),
    );

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** Hoofddocent-only: set or clear the school alias (short informal name). */
export const submitUpdateAlias = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ alias: z.string().max(150).nullable() }))
  .handler(async ({ data }) => {
    const result = toResult(await client.PUT("/school/alias", { body: { alias: data.alias } }));

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

const updateLogoSchema = z.object({
  originalFileId: z.uuid(),
  croppedFileId: z.uuid(),
  x: z.number().int(),
  y: z.number().int(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

/**
 * Persist the school logo. Mirrors portal's `detail.api.logo` action: the client uploads the
 * original + cropped images (via the file-upload endpoint) then submits the resulting file ids
 * plus the crop rectangle here.
 */
export const submitUpdateLogo = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(updateLogoSchema)
  .handler(async ({ data }) => {
    const payload: components["schemas"]["UpdateLogoCommand"] = {
      originalFileId: data.originalFileId,
      croppedFileId: data.croppedFileId,
      x: data.x,
      y: data.y,
      width: data.width,
      height: data.height,
    };

    const result = toResult(await client.PUT("/school/logo", { body: payload }));

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });
